/**
 * Iter242 · Perplexity Ask Bar
 *
 * Ad-hoc web-grounded research page for the operator. Any B2B question
 * ("who is the VP Facilities at CocaCola Miami?", "latest Allied
 * Universal FL contracts") returns a short synthesised answer with
 * clickable citations. Non-streaming — Perplexity call takes ~5-10s.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Search, Loader2, ExternalLink, Sparkles, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SmithAsk() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("smith.ask.history") || "[]"); }
    catch { return []; }
  });
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const question = q.trim();
    if (question.length < 3) return;
    setLoading(true); setError(""); setResult(null); setCopied(false);
    try {
      const r = await axios.post(`${API}/smith/perplexity/ask`, { question });
      setResult(r.data);
      const next = [{ q: question, at: new Date().toISOString() },
                      ...history.filter((h) => h.q !== question)].slice(0, 8);
      setHistory(next);
      localStorage.setItem("smith.ask.history", JSON.stringify(next));
    } catch (ex) {
      setError(ex?.response?.data?.detail || ex.message || "Ask failed");
    } finally { setLoading(false); }
  };

  const copyAnswer = () => {
    if (!result?.answer) return;
    navigator.clipboard.writeText(result.answer);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen text-white"
         style={{ background: "linear-gradient(180deg,#0B1220 0%,#000 100%)" }}
         data-testid="smith-ask-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5">
        {/* Header */}
        <header className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ background: "linear-gradient(135deg,#7C3AED 0%,#0F172A 100%)" }}>
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-[20px] sm:text-[24px] font-extrabold leading-tight">
              Ask Bar <span className="text-[11px] text-purple-300 font-bold uppercase tracking-widest ml-1">Perplexity Sonar Pro</span>
            </h1>
            <p className="text-[11px] sm:text-[12px] text-slate-400">
              Web-grounded B2B research · retorna resposta + fontes citáveis em ~5-10s
            </p>
          </div>
        </header>

        {/* Ask input */}
        <form onSubmit={submit}
              data-testid="ask-form"
              className="rounded-2xl border border-purple-500/30 p-3 sm:p-4"
              style={{ background: "rgba(15,23,42,0.7)" }}>
          <div className="flex items-start gap-2">
            <Search className="w-4 h-4 text-purple-300 mt-3 shrink-0" />
            <textarea
              ref={inputRef}
              data-testid="ask-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Ex: "Quem é o VP Facilities da Coca-Cola Miami em 2026?" · "Últimos contratos da SecurTec of Florida" · "Cold storage warehouses em Tampa com >250k sqft"'
              rows={2}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e);
              }}
              className="flex-1 bg-transparent outline-none resize-none text-[14px] placeholder-slate-500 min-h-[64px]" />
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
            <span className="text-[10.5px] text-slate-500">
              Ctrl/Cmd + Enter para enviar · custo ~$0.02 por pergunta
            </span>
            <button
              type="submit"
              disabled={loading || q.trim().length < 3}
              data-testid="ask-submit-btn"
              className="h-9 px-4 rounded-lg text-[12px] font-extrabold uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: loading ? "#4B5563" : "linear-gradient(135deg,#7C3AED 0%,#DC2626 100%)" }}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {loading ? "Pesquisando…" : "Ask"}
            </button>
          </div>
        </form>

        {/* Error */}
        {error && (
          <div data-testid="ask-error"
               className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px] text-red-200">
            {error}
          </div>
        )}

        {/* Result */}
        {result && (
          <section data-testid="ask-result"
                   className="rounded-2xl border border-white/10 p-4 space-y-3"
                   style={{ background: "rgba(15,23,42,0.7)" }}>
            <div className="flex items-start justify-between gap-3">
              <span className="text-[10.5px] uppercase tracking-widest text-purple-300 font-extrabold">
                Resposta · {result.model}
              </span>
              <button type="button" onClick={copyAnswer}
                      data-testid="ask-copy-btn"
                      className="text-[10.5px] text-slate-400 hover:text-white inline-flex items-center gap-1">
                {copied ? <><Check className="w-3 h-3 text-emerald-300" /> Copiado</>
                        : <><Copy className="w-3 h-3" /> Copiar</>}
              </button>
            </div>
            <div data-testid="ask-answer"
                 className="text-[13.5px] leading-relaxed whitespace-pre-wrap text-slate-100">
              {result.answer}
            </div>
            {(result.sources || []).length > 0 && (
              <div className="pt-2 border-t border-white/5 space-y-1.5">
                <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-extrabold">
                  Fontes ({result.sources.length})
                </span>
                <ul className="space-y-1">
                  {result.sources.map((s, i) => (
                    <li key={`${s.url}-${i}`}
                        data-testid={`ask-source-${i}`}>
                      <a href={s.url} target="_blank" rel="noreferrer"
                         className="text-[12px] text-blue-300 hover:text-white inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        {s.title || s.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Recent history */}
        {history.length > 0 && !result && !loading && (
          <section data-testid="ask-history"
                   className="rounded-2xl border border-white/10 p-4 space-y-2"
                   style={{ background: "rgba(15,23,42,0.7)" }}>
            <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-extrabold">
              Perguntas recentes
            </span>
            <ul className="space-y-1">
              {history.map((h, i) => (
                <li key={i}>
                  <button type="button" onClick={() => { setQ(h.q); inputRef.current?.focus(); }}
                          data-testid={`ask-history-${i}`}
                          className="text-[12px] text-slate-300 hover:text-white text-left">
                    · {h.q}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
